import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../Common/Layout/AdminHeader';
import { rexTechTheme } from '../Common/Theme';
import {
    Mail,
    Phone,
    MapPin,
    Calendar,
    Save,
    Camera,
    Shield,
    CheckCircle,
    AlertCircle,
    Building,
    Globe,
    Wallet
} from 'lucide-react';

// Import your API configuration
import config from '../Common/Configurations/APIConfig';

const Profile = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [profileImage, setProfileImage] = useState(null);
    const [previewImage, setPreviewImage] = useState('https://via.placeholder.com/150');
    const [formData, setFormData] = useState({
        // Company fields from API
        companyId: '',
        userName: '',
        companyName: '',
        companyEmail: '',
        phoneNumber: '',
        companyAddress: '',
        city: '',
        country: '',
        companyType: 'COMPANY',
        currency: 'INR',
        logoUrl: '',
        isActive: true
    });

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Fetch company data when component mounts
    useEffect(() => {
        fetchCompanyData();
    }, []);

    // ================= API Calls =================

    const fetchCompanyData = async () => {
        try {
            setIsFetching(true);
            setErrorMessage('');

            // Get user ID from localStorage or context (adjust based on your auth setup)
            const userId = localStorage.getItem('userId'); //   const userId = localStorage.getItem('userId') || 'current-user-id';
            

            // Fetch company data by user ID
            const response = await axios.get(
                `${config.APIACTIVEURL}${config.GETCOMPANYBYUSERID}/${userId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // Add authorization token if required
                        // 'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data) {
                const companyData = response.data;
                updateFormWithCompanyData(companyData);
            }
        } catch (error) {
            console.error('Error fetching company data:', error);
            setErrorMessage('Failed to load company information. Please try again.');

            // If you want to show a toast instead
            // showToast('error', 'Failed to load company data');
        } finally {
            setIsFetching(false);
        }
    };

    const updateFormWithCompanyData = (companyData) => {
        setFormData(prev => ({
            ...prev,
            companyId: companyData.companyId || '',
            userName: companyData.userName || '',
            companyName: companyData.companyName || '',
            companyEmail: companyData.email || '',
            phoneNumber: companyData.phoneNumber || '',
            companyAddress: companyData.address || '',
            city: companyData.city || '',
            country: companyData.country || '',
            companyType: companyData.companyType || 'COMPANY',
            currency: companyData.currency || 'INR',
            logoUrl: companyData.logoUrl || '',
            isActive: companyData.isActive !== undefined ? companyData.isActive : true
        }));

        // If there's a logo URL, use it as preview
        if (companyData.logoUrl) {
            setPreviewImage(companyData.logoUrl);
        }
    };

    const updateCompany = async (companyData) => {
        try {
            setIsLoading(true);
            setErrorMessage('');
            setSuccessMessage('');

            // Prepare the data for API (match your backend model structure)
            const updatePayload = {
                companyId: companyData.companyId,
                userName: companyData.userName,
                companyName: companyData.companyName,
                email: companyData.companyEmail,
                phoneNumber: companyData.phoneNumber,
                address: companyData.companyAddress,
                city: companyData.city,
              
                country: companyData.country,
                companyType: companyData.companyType,
                currency: companyData.currency,
                isActive: companyData.isActive
            };

            const response = await axios.put(
                `${config.APIACTIVEURL}${config.UPDATECOMPANY}`,
                updatePayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.status === 200) {
                setSuccessMessage('Company information updated successfully!');

                // If you want to show a toast
                // showToast('success', 'Company updated successfully');

                // Refresh the data to get any server-side changes
                setTimeout(() => fetchCompanyData(), 1000);
            }
        } catch (error) {
            console.error('Error updating company:', error);

            // Handle different types of errors
            if (error.response) {
                // Server responded with error status
                setErrorMessage(`Update failed: ${error.response.data?.message || 'Server error'}`);
            } else if (error.request) {
                // Request made but no response received
                setErrorMessage('Network error. Please check your connection.');
            } else {
                // Something else went wrong
                setErrorMessage('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ================= Form Handling =================

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Handle company-specific field updates
        if (name.startsWith('company')) {
            const companyField = name.replace('company', '').charAt(0).toLowerCase() +
                name.replace('company', '').slice(1);
            setFormData(prev => ({
                ...prev,
                [companyField]: value
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Clear messages when user starts typing
        setSuccessMessage('');
        setErrorMessage('');
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Company validations
        if (!formData.companyName.trim()) {
            newErrors.companyName = 'Company name is required';
        }

        if (!formData.companyEmail.trim()) {
            newErrors.companyEmail = 'Company email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)) {
            newErrors.companyEmail = 'Please enter a valid company email';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Update company information
        await updateCompany(formData);
    };

    // ================= UI Components =================

    const CompanyInfoSection = () => (
        <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.xl,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            marginBottom: rexTechTheme.spacing.lg
        }}>
            <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
            }}>
                <Building size={24} />
                Company Information
            </h3>

            {isFetching ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: rexTechTheme.spacing.xl,
                    color: rexTechTheme.colors.primary.main
                }}>
                    <div style={{
                        width: '30px',
                        height: '30px',
                        border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                        borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginRight: rexTechTheme.spacing.md
                    }} />
                    <span>Loading company data...</span>
                </div>
            ) : (
                <>
                    {/* Status Messages */}
                    {successMessage && (
                        <div style={{
                            padding: rexTechTheme.spacing.md,
                            backgroundColor: rexTechTheme.colors.primary.lightest,
                            border: `1px solid ${rexTechTheme.colors.primary.light}`,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            color: rexTechTheme.colors.primary.dark,
                            marginBottom: rexTechTheme.spacing.lg,
                            display: 'flex',
                            alignItems: 'center',
                            gap: rexTechTheme.spacing.sm
                        }}>
                            <CheckCircle size={18} />
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div style={{
                            padding: rexTechTheme.spacing.md,
                            backgroundColor: rexTechTheme.colors.accent.lightest,
                            border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            color: rexTechTheme.colors.accent.orange,
                            marginBottom: rexTechTheme.spacing.lg,
                            display: 'flex',
                            alignItems: 'center',
                            gap: rexTechTheme.spacing.sm
                        }}>
                            <AlertCircle size={18} />
                            {errorMessage}
                        </div>
                    )}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: rexTechTheme.spacing.lg,
                        marginBottom: rexTechTheme.spacing.lg,
                        '@media (max-width: 768px)': {
                            gridTemplateColumns: '1fr'
                        }
                    }}>
                        {/* Company Name */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Company Name
                                <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${errors.companyName ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                            {errors.companyName && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.xs,
                                    marginTop: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.accent.orange,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    <AlertCircle size={14} />
                                    <span>{errors.companyName}</span>
                                </div>
                            )}
                        </div>

                        {/* Company Email */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Company Email
                                <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
                            </label>
                            <input
                                type="email"
                                name="companyEmail"
                                value={formData.companyEmail}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${errors.companyEmail ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                            {errors.companyEmail && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.xs,
                                    marginTop: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.accent.orange,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    <AlertCircle size={14} />
                                    <span>{errors.companyEmail}</span>
                                </div>
                            )}
                        </div>

                        {/* Company Phone */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Company Phone
                            </label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                        </div>

                       

                        {/* Address */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Address
                            </label>
                            <input
                                type="text"
                                name="companyAddress"
                                value={formData.companyAddress}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                        </div>

                        {/* City */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                City
                            </label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                        </div>

                       
                        {/* Country */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                <Globe size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Country
                            </label>
                            <input
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                        </div>

                        {/* Currency */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                <Wallet size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Currency
                            </label>
                            <select
                                name="currency"
                                value={formData.currency}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    backgroundColor: rexTechTheme.colors.neutral.white,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            >
                                <option value="INR">Indian Rupee (INR)</option>
                                <option value="USD">US Dollar (USD)</option>
                                <option value="EUR">Euro (EUR)</option>
                                <option value="GBP">British Pound (GBP)</option>
                                <option value="JPY">Japanese Yen (JPY)</option>
                            </select>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: rexTechTheme.spacing.md,
                        marginTop: rexTechTheme.spacing.xl,
                        paddingTop: rexTechTheme.spacing.lg,
                        borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`
                    }}>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/dashboard')}
                            style={{
                                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                color: rexTechTheme.colors.neutral.darkGray,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                ':hover': {
                                    backgroundColor: rexTechTheme.colors.neutral.gray
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            onClick={handleSubmit}
                            disabled={isLoading}
                            style={{
                                padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                color: rexTechTheme.colors.neutral.white,
                                border: 'none',
                                borderRadius: rexTechTheme.borderRadius.medium,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                opacity: isLoading ? 0.7 : 1,
                                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                ':hover': isLoading ? {} : {
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                }
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }} />
                                    <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Save Company Changes
                                </>
                            )}
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <AdminHeader />

            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                Company Profile
                            </h1>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                margin: 0
                            }}>
                                Manage your company information and details
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: rexTechTheme.spacing.sm,
                            padding: rexTechTheme.spacing.md,
                            backgroundColor: rexTechTheme.colors.primary.lightest,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            border: `1px solid ${rexTechTheme.colors.primary.lighter}`
                        }}>
                            <Shield size={20} color={rexTechTheme.colors.primary.main} />
                            <span style={{
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.medium
                            }}>
                                Administrator Account
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: rexTechTheme.spacing.xl,
                    '@media (max-width: 768px)': {
                        gridTemplateColumns: '1fr'
                    }
                }}>
                    {/* Left Column: Profile Image & Info */}
                    <div>
                        <div style={{
                            backgroundColor: rexTechTheme.colors.neutral.white,
                            borderRadius: rexTechTheme.borderRadius.large,
                            padding: rexTechTheme.spacing.xl,
                            boxShadow: rexTechTheme.shadows.light,
                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                            textAlign: 'center',
                            marginBottom: rexTechTheme.spacing.lg
                        }}>
                            <div style={{ position: 'relative', marginBottom: rexTechTheme.spacing.lg }}>
                                <div style={{
                                    width: '150px',
                                    height: '150px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    margin: '0 auto',
                                    border: `4px solid ${rexTechTheme.colors.primary.lightest}`,
                                    position: 'relative'
                                }}>
                                    <img
                                        src={previewImage}
                                        alt="Company Logo"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />

                                    <label htmlFor="profile-image-upload" style={{
                                        position: 'absolute',
                                        bottom: '10px',
                                        right: '10px',
                                        backgroundColor: rexTechTheme.colors.primary.main,
                                        color: rexTechTheme.colors.neutral.white,
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        ':hover': {
                                            backgroundColor: rexTechTheme.colors.primary.dark,
                                            transform: 'scale(1.1)'
                                        }
                                    }}>
                                        <Camera size={18} />
                                    </label>

                                    <input
                                        id="profile-image-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            </div>

                            <h3 style={{
                                color: rexTechTheme.colors.primary.dark,
                                fontSize: rexTechTheme.typography.fontSize.xlarge,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                {formData.companyName || 'Company Name'}
                            </h3>






                        </div>

                        {/* Quick Stats */}
                        <div style={{
                            backgroundColor: rexTechTheme.colors.neutral.white,
                            borderRadius: rexTechTheme.borderRadius.large,
                            padding: rexTechTheme.spacing.lg,
                            boxShadow: rexTechTheme.shadows.light,
                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                        }}>
                            <h4 style={{
                                color: rexTechTheme.colors.primary.dark,
                                fontSize: rexTechTheme.typography.fontSize.large,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                marginBottom: rexTechTheme.spacing.md
                            }}>
                                Company Status
                            </h4>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: rexTechTheme.spacing.sm
                            }}>
                                <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                                    Account Status:
                                </span>
                                <span style={{
                                    color: formData.isActive ? rexTechTheme.colors.primary.main : rexTechTheme.colors.accent.orange,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                }}>
                                    {formData.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                                    Currency:
                                </span>
                                <span style={{
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.medium
                                }}>
                                    {formData.currency}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Company Information Form */}
                    <div>
                        <CompanyInfoSection />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;